import Yup from '@yup';
import { GridColDefExtended } from '@mui/x-data-grid-pro';
import { BoardQuery, QueryFilter } from '@api/Trinium/types';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { forcedPinnedFields } from '@api/Trinium/Endpoints/BoardQueries/types';
import { DispatchLegColumns } from '@components/DataGrid/Dispatch/types';
import { dispatchColumnDefinitions } from '@components/DataGrid/Dispatch/columns';
import { getColumnLabel } from '@components/DataGrid/types';
import { getEnumEntryByString, getObjectEntryByKey } from '@/Infrastructure/Utilities';

/** Editable fields of a Board Query. Grows as the editor takes on more of the query configuration. */
export interface BoardQueryFormFields {
  name: string;
  /** Ordered column keys. Position in this array is the column's position on the board. */
  columns: (keyof typeof DispatchLegColumns)[];
  /**
   * Fields of the columns pinned to the left of the board, ordered left to right. The grid hoists these ahead of
   * everything in columns, so this array — not columns — decides where a pinned column lands. Also holds the
   * forced columns the editor hides.
   */
  pinnedFields: string[];
  /** Filters the board narrows its legs down by, all of which must hold for a leg to be rendered. */
  filters: QueryFilter[];
}

/** Columns the grid owns outright — they carry no user-facing label, so the editor lists them nowhere. */
export const hiddenColumns: (keyof typeof DispatchLegColumns)[] = ['__check__', 'actionMenu'];

/**
 * Columns every board leads with, in this order. The editor lists them so their position on the board is visible
 * but refuses to reorder, unpin, or remove them.
 */
export const lockedColumns: (keyof typeof DispatchLegColumns)[] = ['orderNumber', 'leg'];

/** Pins the locked columns hold, ahead of any pin the query owns. Nothing the user pins may displace these. */
export const lockedPinnedFields = [...forcedPinnedFields, dispatchColumnDefinitions.leg.field];

/** Field names come from the server, so their casing is not guaranteed to match the column definition's field. */
export function sameField(left: string, right: string): boolean {
  return left.localeCompare(right, 'en', { sensitivity: 'base' }) === 0;
}

/**
 * Two names the board could not tell apart. Casing is ignored because a query is resolved by name in places that
 * match it exactly — two queries differing only in case would leave one of them unreachable.
 */
export function sameName(left: string, right: string): boolean {
  return sameField(left.trim(), right.trim());
}

/**
 * Name a copy arrives with. Already free, so the copy can be saved as it stands, while still reading as a copy of
 * the query it came from; the suffix is what keeps a second copy off the first one's name.
 */
export function copyOfName(name: string, existingNames: string[]): string {
  const base = `COPY OF ${name}`;
  let candidate = base;

  for (let attempt = 2; existingNames.some((existing) => sameName(existing, candidate)); attempt++) {
    candidate = `${base} ${String(attempt)}`;
  }

  return candidate;
}

/**
 * Resolves a column key from the API to its definition. Unknown keys are possible because the query's columns
 * come from the server; the board discards them too (see getDispatchGridColDefs).
 */
export function getColumnDefinition(column: keyof typeof DispatchLegColumns) {
  const validColumn = getEnumEntryByString(DispatchLegColumns, column);
  return validColumn ? getObjectEntryByKey(dispatchColumnDefinitions, validColumn) : undefined;
}

/** Position of a column within the pinned fields, or -1 when it is not pinned. */
export function findPinnedIndex(pinnedFields: string[], definition: GridColDefExtended<DispatchLeg>): number {
  return pinnedFields.findIndex((pinned) => sameField(pinned, definition.field));
}

export interface SelectableColumn {
  column: keyof typeof DispatchLegColumns;
  field: string;
  label: string;
}

/** Every dispatch column a query can pick up, ordered by the label the board displays so it reads as a lookup list. */
export const selectableColumns: SelectableColumn[] = (Object.keys(DispatchLegColumns) as (keyof typeof DispatchLegColumns)[])
  .reduce<SelectableColumn[]>((accumulator, column) => {
    const definition = hiddenColumns.includes(column) || lockedColumns.includes(column) ? undefined : getColumnDefinition(column);

    if (definition) {
      accumulator.push({ column, field: definition.field, label: getColumnLabel(definition) });
    }
    return accumulator;
  }, [])
  .sort((left, right) => left.label.localeCompare(right.label, 'en', { sensitivity: 'base' }));

export function toFormFields(query: BoardQuery): BoardQueryFormFields {
  // orderNumber is dropped because the board appends and pins it itself — the editor shows it without saving it
  const columns = query.configuration.columns.filter((column) => column !== 'orderNumber' && !hiddenColumns.includes(column));
  const queryPins = (query.configuration.pinnedCols.left ?? []).filter(
    (pinned) => !lockedPinnedFields.some((locked) => sameField(locked, pinned))
  );

  return {
    name: query.name,
    columns: columns.includes('leg') ? columns : ['leg', ...columns],
    pinnedFields: [...lockedPinnedFields, ...queryPins],
    filters: query.configuration.filters
  };
}

export const queryNameSchema = Yup.string().trim().required('A query name is required.').max(50, 'Query names are limited to 50 characters.');

/**
 * A query being created is held to two rules a saved one is not: its name has to be free, and it has to narrow the
 * board down to something — a query with no filters is what ALL UNDELIVERED already is. Neither rule can be applied
 * to saved queries: their names are not editable, and existing ones are allowed to carry no filters.
 */
export function getBoardQueryValidationSchema(isNewQuery: boolean, existingNames: string[]) {
  return Yup.object<BoardQueryFormFields>().shape({
    name: isNewQuery
      ? queryNameSchema.test('unique', 'This name is already in use.', (value) => {
          // required already covers an empty name; this test only speaks to collisions
          if (!value) return true;

          return !existingNames.some((existing) => sameName(existing, value));
        })
      : queryNameSchema,
    columns: Yup.array().min(1, 'A query needs at least one column.'),
    filters: isNewQuery ? Yup.array().min(1, 'A query needs at least one filter.') : Yup.array()
  });
}
